/**
 * CashRegisterModal Component
 * 
 * Modal obrigatório para abertura de caixa ao acessar pedidos.
 * 
 * Funcionalidades:
 * - Modal obrigatório (não pode fechar sem ação)
 * - Campo "Valor de Abertura" (formato monetário)
 * - Campo "Observações" (textarea)
 * - Botão "Sair do pedido" (fecha modal e redireciona)
 * - Botão "Abrir Caixa" (chama API e fecha modal)
 * - Validações e loading states
 */

import { useState } from 'react'
import { Calculator, X } from 'lucide-react'
import { cashRegisterService } from '../../../services/erpApi'
import './CashRegisterModal.css'

function CashRegisterModal({
  isOpen,
  onClose,
  onOpen,
  required = true
}) {
  const [openingAmount, setOpeningAmount] = useState('')
  const [observation, setObservation] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Formatar valor monetário
  const formatCurrency = (value) => {
    // Remove tudo que não é número
    const numbers = value.replace(/\D/g, '')
    // Converte para centavos e depois para reais
    const cents = parseInt(numbers) || 0
    const reais = (cents / 100).toFixed(2)
    // Formata com separador de milhar
    return parseFloat(reais).toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
  }

  const handleAmountChange = (e) => {
    const value = e.target.value
    const formatted = formatCurrency(value)
    setOpeningAmount(formatted)
  }

  const handleOpenCashRegister = async () => {
    setError('')
    
    // Validar valor
    const amountValue = parseFloat(openingAmount.replace(/\./g, '').replace(',', '.'))
    if (isNaN(amountValue) || amountValue < 0) {
      setError('Valor de abertura deve ser um número positivo')
      return
    }

    setLoading(true)

    try {
      const response = await cashRegisterService.open(
        amountValue,
        observation.trim() || null
      )

      if (response.success) {
        // Limpar formulário
        setOpeningAmount('')
        setObservation('')
        setError('')
        
        // Chamar callback de sucesso
        if (onOpen) {
          onOpen(response.data)
        }
      } else {
        setError(response.message || 'Erro ao abrir caixa')
      }
    } catch (err) {
      console.error('Erro ao abrir caixa:', err)
      setError(
        err.response?.data?.message || 
        err.message || 
        'Erro ao abrir caixa. Tente novamente.'
      )
    } finally {
      setLoading(false)
    }
  }

  const handleExit = () => {
    if (!required) {
      onClose()
      return
    }

    // Se for obrigatório, confirmar saída
    if (window.confirm('Deseja realmente sair? É necessário abrir o caixa para acessar os pedidos.')) {
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <div className="cash-register-modal-overlay">
      <div className="cash-register-modal-content">
        <div className="cash-register-modal-header">
          <div className="cash-register-modal-header-left">
            <div className="cash-register-modal-icon">
              <Calculator size={28} />
            </div>
            <div>
              <h2 className="cash-register-modal-title">
                Abertura de Caixa
              </h2>
              <p className="cash-register-modal-subtitle">Informe o valor inicial do caixa</p>
            </div>
          </div>
          {!required && (
            <button
              className="cash-register-modal-close"
              onClick={handleExit}
              aria-label="Fechar modal"
            >
              <X size={24} />
            </button>
          )}
        </div>

        <div className="cash-register-modal-body">
          {required && (
            <div className="cash-register-modal-warning">
              <strong>⚠️ Atenção:</strong> É necessário abrir o caixa para acessar os pedidos.
            </div>
          )}

          <div className="cash-register-modal-form">
            <div className="cash-register-form-group">
              <label htmlFor="openingAmount" className="cash-register-form-label">
                Valor de Abertura <span className="cash-register-required">*</span>
              </label>
              <div className="cash-register-input-wrapper">
                <span className="cash-register-currency">R$</span>
                <input
                  id="openingAmount"
                  type="text"
                  className="cash-register-form-input cash-register-currency-input"
                  value={openingAmount}
                  onChange={handleAmountChange}
                  placeholder="0,00"
                  disabled={loading}
                  autoFocus
                />
              </div>
              <small className="cash-register-form-help">
                Informe o valor inicial do caixa
              </small>
            </div>

            <div className="cash-register-form-group">
              <label htmlFor="observation" className="cash-register-form-label">
                Observações
              </label>
              <textarea
                id="observation"
                className="cash-register-form-input cash-register-textarea"
                value={observation}
                onChange={(e) => setObservation(e.target.value)}
                placeholder="Observações sobre a abertura do caixa (opcional)"
                rows="4"
                disabled={loading}
              />
            </div>

            {error && (
              <div className="cash-register-error-message">
                {error}
              </div>
            )}
          </div>
        </div>

        <div className="cash-register-modal-footer">
          <button
            type="button"
            className="cash-register-btn cash-register-btn-secondary"
            onClick={handleExit}
            disabled={loading}
          >
            Sair do pedido
          </button>
          <button
            type="button"
            className="cash-register-btn cash-register-btn-primary"
            onClick={handleOpenCashRegister}
            disabled={loading || !openingAmount}
          >
            {loading ? 'Abrindo...' : 'Abrir Caixa'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default CashRegisterModal

